import React, { forwardRef, useImperativeHandle } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { submitAnalysisData } from "../../../Services/apiService";
import { isCellFilled, createSnapshot } from "./analysisDataUtils";
import StatusPill from "../../../components/StatusPill";

const InfoRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      py: 0.75,
      gap: 2,
      borderBottom: "1px solid #f0f0f0",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} sx={{ textAlign: "right", wordBreak: "break-word" }}>
      {value || "—"}
    </Typography>
  </Box>
);

// Ported from src/pages/AddAnalysisData.js. All the h2/ch4/co2 sample state,
// additionalInfo/notes, savedSnapshot/hasServerData, and the gating flags are
// now owned by the parent's useWorkspaceData hook (passed down as props),
// since the Report tab also needs them. This tab only owns UI-local state
// (validation error outlines, the save-success banner) and the field editing
// handlers.
const AddDataTab = forwardRef(function AddDataTab(
  {
    formData,
    analysisData,
    setAnalysisData,
    additionalInfo,
    setAdditionalInfo,
    notes,
    setNotes,
    setSavedSnapshot,
    setHasServerData,
    saving,
    setSaving,
    loading,
    socketConnected,
    isTableComplete,
    isDirty,
    canSave,
    canCancel,
    canGenerateReport,
    savedSnapshot,
    orderId,
    testId,
    onSwitchTab,
    onNotify,
  },
  ref
) {
  const [validationErrors, setValidationErrors] = React.useState({});
  const [success, setSuccess] = React.useState(false);

  const testInfo = formData?.test_information;

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (err) {
      console.error("Date formatting error:", err);
      return dateString;
    }
  };

  const handleInputChange = (type, index, value) => {
    const isValidInput = /^-$|^$|^\d*\.?\d*$/.test(value);
    if (!isValidInput) return;

    setAnalysisData((prev) => ({
      ...prev,
      [`${type}_values`]: prev[`${type}_values`].map((item, i) => (i === index ? value : item)),
    }));
  };

  const handleKeyDown = (e, currentType, currentIndex) => {
    const types = ["h2", "ch4", "co2"];
    const currentTypeIndex = types.indexOf(currentType);

    let nextType = currentType;
    let nextIndex = currentIndex;

    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        if (currentTypeIndex < types.length - 1) {
          nextType = types[currentTypeIndex + 1];
        } else if (currentIndex < 9) {
          nextType = types[0];
          nextIndex = currentIndex + 1;
        }
        break;
      case "ArrowLeft":
        e.preventDefault();
        if (currentTypeIndex > 0) {
          nextType = types[currentTypeIndex - 1];
        } else if (currentIndex > 0) {
          nextType = types[types.length - 1];
          nextIndex = currentIndex - 1;
        }
        break;
      case "ArrowDown":
        e.preventDefault();
        if (currentIndex < 9) nextIndex = currentIndex + 1;
        break;
      case "ArrowUp":
        e.preventDefault();
        if (currentIndex > 0) nextIndex = currentIndex - 1;
        break;
      default:
        return;
    }

    const nextInputId = `${nextType}-${nextIndex}`;
    const nextInput = document.getElementById(nextInputId);
    if (nextInput) nextInput.focus();
  };

  const handleAdditionalInfoChange = (field, value) => {
    setAdditionalInfo((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const errors = {};
    const missingFields = [];

    let hasEmptyTableValues = false;
    for (let i = 0; i < 10; i++) {
      if (
        !isCellFilled(analysisData.h2_values[i]) ||
        !isCellFilled(analysisData.ch4_values[i]) ||
        !isCellFilled(analysisData.co2_values[i])
      ) {
        hasEmptyTableValues = true;
        errors[`table_row_${i}`] = true;
      }
    }

    if (hasEmptyTableValues) {
      missingFields.push('All table values (H₂, CH₄, CO₂) - use "-" for missing values');
    }

    setValidationErrors(errors);

    if (missingFields.length > 0) {
      onNotify?.(`Please fill missing fields: ${missingFields.join(", ")}`, "warning");
      return false;
    }

    return true;
  };

  const handleDiscardChanges = () => {
    if (!savedSnapshot) return;
    const snap = JSON.parse(savedSnapshot);
    setAnalysisData({
      h2_values: [...snap.h2],
      ch4_values: [...snap.ch4],
      co2_values: [...snap.co2],
    });
    setAdditionalInfo({ ...snap.additionalInfo });
    setNotes(snap.notes);
    setValidationErrors({});
    setSuccess(false);
    onNotify?.("Changes discarded.", "info");
  };

  const handleSaveAnalysisData = async () => {
    if (!validateForm()) return;

    setSaving(true);

    try {
      const analysisDataFormatted = {};
      for (let i = 0; i < 10; i++) {
        const h2Value = analysisData.h2_values[i];
        const ch4Value = analysisData.ch4_values[i];
        const co2Value = analysisData.co2_values[i];

        analysisDataFormatted[`sample_${i + 1}`] = {
          h2: h2Value === "-" ? "-" : parseFloat(h2Value) || 0,
          ch4: ch4Value === "-" ? "-" : parseFloat(ch4Value) || 0,
          co2: co2Value === "-" ? "-" : parseFloat(co2Value) || 0,
        };
      }

      const jsonData = {
        additional_info: {
          ...additionalInfo,
          date_of_birth: formData?.mandatory_information?.date_of_birth,
        },
        analysis_data: analysisDataFormatted,
        notes: notes.trim() || null,
        metadata: { created_at: new Date().toISOString() },
      };

      await submitAnalysisData(orderId, testId, jsonData);

      setSuccess(true);
      setSavedSnapshot(
        createSnapshot(
          analysisData.h2_values,
          analysisData.ch4_values,
          analysisData.co2_values,
          additionalInfo,
          notes
        )
      );
      setHasServerData(true);
      onNotify?.("Analysis data saved successfully!", "success");
    } catch (err) {
      console.error("❌ Error saving analysis data:", err);
      onNotify?.("Error saving analysis data. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateReport = () => {
    if (!canGenerateReport) {
      if (!isTableComplete) {
        onNotify?.('Please fill all 30 sample values (use "-" for missing data).', "warning");
      } else if (isDirty) {
        onNotify?.("Please save your changes before generating the report.", "warning");
      } else {
        onNotify?.("Please save analysis data first before generating report.", "warning");
      }
      return;
    }

    if (!validateForm()) return;

    onSwitchTab?.();
  };

  useImperativeHandle(ref, () => ({
    save: handleSaveAnalysisData,
  }));

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "50vh",
          py: 4,
          px: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h6">Loading analysis data...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        py: { xs: 2, md: 3 },
        px: { xs: 2, md: 3 },
      }}
    >
      {success && (
        <Box sx={{ maxWidth: 1100, mx: "auto", mb: 2 }}>
          <Alert severity="success" onClose={() => setSuccess(false)}>
            Analysis data saved successfully!
          </Alert>
        </Box>
      )}

      {formData && (
        <Box
          sx={{
            maxWidth: 1100,
            mx: "auto",
            mb: 3,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
          }}
        >
          <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "16px" }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                Test Information
              </Typography>
              <InfoRow label="Order Number" value={formData.test_information?.order_number} />
              <InfoRow label="Test Identifier" value={formData.test_information?.test_identifier} />
              <InfoRow label="Test Type" value={formData.test_information?.title} />
              <InfoRow label="Test Substrate" value={formData.test_information?.substrate} />
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "16px" }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                Additional Information
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.5,
                  "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                }}
              >
                <TextField
                  size="small"
                  variant="outlined"
                  label="Birthdate"
                  value={formatDateForDisplay(formData.mandatory_information?.date_of_birth)}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  size="small"
                  variant="outlined"
                  label="Date of Evaluation"
                  type="date"
                  value={additionalInfo.date_evaluation}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  variant="outlined"
                  label="Date of Sampling"
                  type="date"
                  value={additionalInfo.date_of_sampling}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  variant="outlined"
                  label="Evaluated by"
                  placeholder="Enter evaluator name"
                  value={additionalInfo.evaluated_by}
                  onChange={(e) => handleAdditionalInfoChange("evaluated_by", e.target.value)}
                  error={validationErrors.evaluated_by}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {formData && (
        <Box sx={{ maxWidth: 1100, mx: "auto" }}>
          <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "16px" }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Analysis Data Entry
                </Typography>
                <StatusPill connected={socketConnected} />
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                Enter values for all 30 cells (10 samples × 3 gases). Use "-" for missing data.
              </Typography>

              {isTableComplete && (
                <Box
                  sx={{
                    mb: 2,
                    px: 2,
                    py: 1.25,
                    borderRadius: 1.5,
                    bgcolor: "#e8f5e9",
                    border: "1px solid #a5d6a7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: "#1b5e20", fontWeight: 500 }}>
                      All sample values complete.
                      {isDirty ? " Save your changes, then generate the report." : " You can generate the report now."}
                    </Typography>
                  </Box>
                  {canGenerateReport && (
                    <Button size="small" variant="contained" color="success" onClick={handleGenerateReport}>
                      View Report →
                    </Button>
                  )}
                </Box>
              )}

              <TableContainer component={Paper} elevation={0} sx={{ mb: 2, border: "1px solid #e2e8f0", borderRadius: 1.5 }}>
                <Table size="small" aria-label="analysis data table">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.75rem", py: 0.75, width: 56 }}>
                        #
                      </TableCell>
                      {["H₂", "CH₄", "CO₂"].map((gas) => (
                        <TableCell key={gas} align="center" sx={{ fontWeight: 600, fontSize: "0.75rem", py: 0.75 }}>
                          {gas}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.from({ length: 10 }, (_, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:nth-of-type(odd)": { backgroundColor: "#fafbfc" },
                          "& td": { py: 0.5, px: 0.75 },
                        }}
                      >
                        <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.75rem", color: "text.secondary" }}>
                          {index + 1}
                        </TableCell>
                        {["h2", "ch4", "co2"].map((type) => (
                          <TableCell key={type}>
                            <TextField
                              id={`${type}-${index}`}
                              fullWidth
                              size="small"
                              placeholder="—"
                              value={analysisData[`${type}_values`][index]}
                              onChange={(e) => handleInputChange(type, index, e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, type, index)}
                              error={
                                validationErrors[`table_row_${index}`] &&
                                !isCellFilled(analysisData[`${type}_values`][index])
                              }
                              inputProps={{
                                style: { fontSize: "0.8125rem", padding: "6px 8px", textAlign: "center" },
                              }}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  mt: 1,
                  pt: 2,
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  gap: 2,
                  alignItems: "stretch",
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Notes"
                  multiline
                  placeholder="Additional notes or observations..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    "& .MuiInputBase-root": {
                      flex: 1,
                      alignItems: "flex-start",
                      fontSize: "0.8125rem",
                      bgcolor: "#f8fafc",
                    },
                    "& .MuiInputBase-inputMultiline": {
                      height: "100% !important",
                      overflow: "auto",
                      boxSizing: "border-box",
                    },
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    minWidth: { md: 200 },
                    flexShrink: 0,
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    onClick={handleDiscardChanges}
                    disabled={!canCancel}
                    sx={{ fontSize: "0.75rem", py: 0.75, borderColor: "#d1d5db", color: "#6b7280" }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={handleSaveAnalysisData}
                    disabled={!canSave}
                    sx={{ fontSize: "0.75rem", py: 0.75 }}
                  >
                    {saving ? "Saving..." : "Save Analysis"}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    onClick={handleGenerateReport}
                    disabled={!canGenerateReport}
                    sx={{ fontSize: "0.75rem", py: 0.75 }}
                  >
                    Generate Report
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
});

export default AddDataTab;
